/// <reference lib="dom" />
/// <reference lib="dom.iterable" />
// Ported from Astro transitions/router.ts (transition orchestration half).
// Source: https://raw.githubusercontent.com/withastro/astro/main/packages/astro/src/transitions/router.ts
// Issue: zudolab/zudo-doc#1516 (W3C1), parent epic zudolab/zudo-doc#1510.
//
// Mechanical renames per W1B §13.5:
//   astro:* event names         → zfb:*
//   data-astro-* attributes     → data-zfb-*
//   astro-view-transitions-*    → zfb-view-transitions-*
//   .astro-route-announcer      → .zfb-route-announcer
//   dataset.astroExec           → dataset.zfbExec
//   dataset.astroHistory        → dataset.zfbHistory
//   dataset.astroRerun          → dataset.zfbRerun
//
// Named-cause deviations:
//   - `internalFetchHeaders` import dropped; replaced with `{}` (W1B §13.5 — zfb adapters
//     do not currently expose a per-fetch internal-headers contract).
//   - `prepareForClientOnlyComponents` dropped entirely (W1B §13.5 / W3C2 — DEV-only
//     iframe trick that compensates for Vite per-component CSS injection on hydrate;
//     not applicable to zfb islands which inject CSS via the bundle).
//   - `import.meta.env.SSR` access uses `(import.meta as any).env?.SSR` (no Vite
//     ambient types in zfb-runtime tsconfig — same workaround used in W3B).
//   - `inBrowser` evaluates to `typeof document !== "undefined"` rather than relying
//     on the SSR flag, because the runtime package serves both server- and client-side
//     code; same observable behavior in browser and on SSR.
//   - `announce()` was a TODO stub at W1B port time; W3C3 implemented it and #1063 gave
//     its 60ms timer explicit lifecycle ownership — see the route-announcer block below.
//
// W3C2 additions (this file):
//   - `navigate()` public entry.
//   - `onPopState`, `onScrollEnd`.
//   - Seeding `currentHistoryIndex` from `history.state`, registering popstate /
//     load / scrollend listeners, and marking already-executed scripts with
//     `dataset["zfbExec"] = ""`. W3C2 did all of this in two bare top-level
//     `if (inBrowser)` blocks; #2436 folded both into `init()` (see the two
//     once-guards there) so evaluating this module has no side effect at all.
//
// W3C1 items deferred to — and since implemented in — W3C3:
//   - `announce()` route-announcer implementation (see the route-announcer block below;
//     timer lifecycle ownership hardened in #1063).
//   - Click + form intercept (see `handleClick` / `handleSubmit` / `init()` below).
//
// zfb-only additions (no Astro upstream — #1076):
//   - `onPageShow` pageshow/bfcache re-sync, registered inside the init block alongside
//     popstate/load/scrollend. Verified against live Astro `main`: neither
//     `transitions/router.ts` nor `ClientRouter.astro` has any pageshow/`persisted`
//     handler, so this is original design, not a port. WebKit serves Back after an SPA
//     route change from the bfcache without re-evaluating this module, desyncing the
//     tracked `currentHistoryIndex` from the live history stack; `onPageShow` re-seeds it
//     (and restores scroll) on a persisted restore — see the `onPageShow` block below.
//   - `derivePopDirection` hardens the popstate forward/back calc against an unchanged or
//     missing/NaN `state.index` that a bfcache restore can leave behind (#1076).

import {
  doPreparation,
  doSwap,
  onPageLoad,
  triggerEvent,
  updateScrollPosition,
  type TransitionBeforePreparationEvent,
} from "./events.js";
import { safePushState, safeReplaceState } from "./history-safe.js";
import { detectScriptExecuted } from "./swap-functions.js";
import type { Direction, Fallback, Options, SyncHistoryEntryOptions } from "./types.js";
// Island re-bootstrap and deferred-cancel after body swap (W1B §12.2, §12.5).
// mountNewIslands() is called after runScripts() and before onPageLoad().
// cancelPendingIslands() is called before doSwap() so deferred callbacks
// (rIC/IntersectionObserver) do not fire against orphan elements.
import { cancelPendingIslands, mountNewIslands, unmountIslands } from "@takazudo/zfb/runtime";

type State = {
  index: number;
  scrollX: number;
  scrollY: number;
};
type Navigation = { controller: AbortController };
type Transition = {
  // The view transitions object (API and simulation)
  viewTransition?: ViewTransition;
  // Simulation: Whether transition was skipped
  transitionSkipped: boolean;
  // Simulation: The resolve function of the finished promise
  viewTransitionFinished?: () => void;
};

// Adapter-specific internal fetch headers. zfb adapters do not currently expose this
// contract — the empty object preserves the call-shape so the loop in fetchHTML is a
// no-op until/unless an adapter wires this up.
const internalFetchHeaders: Record<string, string> = {};

// Detect browser context. Astro uses `import.meta.env.SSR === false` (Vite-injected).
// The zfb-runtime tsconfig has no Vite ambient types; checking for `document` is
// behaviorally identical and avoids a Vite type dependency.
const inBrowser = typeof document !== "undefined";

export const supportsViewTransitions = inBrowser && !!document.startViewTransition;

export const transitionEnabledOnThisPage = () =>
  inBrowser && !!document.querySelector('[name="zfb-view-transitions-enabled"]');

// A same-page Back/Forward traversal (two history entries sharing pathname +
// search) is served from the live DOM by the fast-path in transition() — no
// re-fetch, no re-swap, no lifecycle events. A per-request SSR page
// (`prerender = false`) whose server output can differ between two visits to
// the SAME url opts back INTO the fetch by emitting
// <meta name="zfb-traverse-refetch" content="true"> (via <ClientRouter
// traverseRefetch />); skipping the fetch on such a page would pin the stale
// first-render content. Read from the CURRENT document — for a same-page
// traverse the current document IS the target page. Mirrors the
// zfb-prefetch-disabled meta reader. (#1376)
const traverseRefetchOptedOut = () =>
  inBrowser && !!document.querySelector('meta[name="zfb-traverse-refetch"][content="true"]');

const samePage = (thisLocation: URL, otherLocation: URL) =>
  thisLocation.pathname === otherLocation.pathname && thisLocation.search === otherLocation.search;

// The previous navigation that might still be in processing
let mostRecentNavigation: Navigation | undefined;
// The previous transition that might still be in processing
let mostRecentTransition: Transition | undefined;
// When we traverse the history, the window.location is already set to the new location.
// This variable tells us where we came from
let originalLocation: URL;

// Route announcer — ported from Astro's announce(). Each navigation appends a fresh
// aria-live <div> (after removing any prior announcer, so exactly one ever exists) and
// writes the new page title into it 60ms later, so screen readers announce the new page.
//
// zfb deviation from Astro / design decision for #1063 (timer lifecycle ownership):
//   - The 60ms timer id is stored and cleared when a newer navigation supersedes a
//     still-pending announcement, so a superseded page's title is never written to a
//     now-detached announcer. (Astro leaves the timer fire-and-forget.)
//   - The previous announcer is removed before a new one is appended, so they never
//     accumulate. We deliberately do NOT *reuse* one element across navigations (which
//     #1063 floated): a reused live region is detached and re-inserted into the a11y
//     tree on every body swap, and a repeated title would be a no-op text write — both
//     make re-announcement unreliable across screen readers. Creating a fresh empty
//     element and writing text 60ms later is Astro's proven, reliable announce trigger.
//
// The 60ms delay is Astro's magic number: screen readers need to see the element change
// and may miss it if it happens too quickly.
let announceTimer: number | undefined;

const announce = () => {
  // A newer navigation supersedes any still-pending announcement: cancel the old
  // timer so it can't write a now-stale title after this navigation's swap.
  if (announceTimer !== undefined) window.clearTimeout(announceTimer);

  // Keep exactly one announcer: drop any leftover from a prior navigation that the
  // body swap did not already detach.
  document.querySelector(".zfb-route-announcer")?.remove();
  const div = document.createElement("div");
  div.setAttribute("aria-live", "assertive");
  div.setAttribute("aria-atomic", "true");
  div.className = "zfb-route-announcer";
  document.body.append(div);

  announceTimer = window.setTimeout(() => {
    announceTimer = undefined;
    // This timer can outlive its document context — a test-env teardown
    // (vitest/happy-dom) or a real page unload removes `document` (and
    // `location`) before the 60ms elapses. `typeof` is safe even when the
    // global is gone, so bail out instead of throwing an unhandled
    // ReferenceError (#1061); the announcement is moot once the page is gone.
    // Both globals the callback dereferences are checked, not just `document`.
    if (typeof document === "undefined" || typeof location === "undefined") return;
    const title = document.title || document.querySelector("h1")?.textContent || location.pathname;
    div.textContent = title;
  }, 60);
};

const PERSIST_ATTR = "data-zfb-transition-persist";

const attrValueSelector = (attr: string, value: string): string =>
  value === "" ? `[${attr}=""]` : `[${attr}=${CSS.escape(value)}]`;

const querySelectorWithAttrValue = (
  root: ParentNode,
  selectorPrefix: string,
  attr: string,
  value: string,
): Element | null => {
  const escapedMatch = root.querySelector(`${selectorPrefix}${attrValueSelector(attr, value)}`);
  if (escapedMatch) return escapedMatch;
  return (
    Array.from(root.querySelectorAll(`${selectorPrefix}[${attr}]`)).find(
      (el) => el.getAttribute(attr) === value,
    ) ?? null
  );
};
const DIRECTION_ATTR = "data-zfb-transition";
const OLD_NEW_ATTR = "data-zfb-transition-fallback";

let parser: DOMParser;

// The History API does not tell you if navigation is forward or back, so
// you can figure it using an index. On pushState the index is incremented so you
// can use that to determine popstate if going forward or back.
let currentHistoryIndex = 0;

// Once-guard for the navigation bookkeeping below. `init()` sets it too, so a
// later ensureNavigationState() can never clobber the activation-time seed.
let navigationStateSeeded = false;

/**
 * Seed the module's navigation bookkeeping — `originalLocation` (declared
 * without an initializer) and the tracked history index — from the live
 * document, once.
 *
 * Deliberately side-effect free beyond those two variables: it registers no
 * listener, writes no history entry, never scrolls, and never marks scripts.
 * All of that belongs to {@link init}. Its only job is to keep an init-less
 * `navigate()` / `syncHistoryEntry()` from reading `undefined` or stamping a
 * fresh entry with the module default index — using the router without
 * activating it is documented as unsupported, but it must not throw and must
 * not mis-stamp. An existing finite `history.state.index` is ADOPTED, never
 * reset: on a page whose entry is already index 7, the next push must be 8. (#2436)
 */
function ensureNavigationState(): void {
  if (navigationStateSeeded || !inBrowser) return;
  navigationStateSeeded = true;
  originalLocation = new URL(location.href);
  const index = history.state?.index;
  if (Number.isFinite(index)) currentHistoryIndex = index;
}

// returns the contents of the page or null if the router can't deal with it.
async function fetchHTML(
  href: string,
  init?: RequestInit,
): Promise<null | { html: string; redirected?: string; mediaType: DOMParserSupportedType }> {
  try {
    // Apply adapter-specific headers for internal fetches
    const headers = new Headers(init?.headers);
    for (const [key, value] of Object.entries(internalFetchHeaders) as [string, string][]) {
      headers.set(key, value);
    }
    const res = await fetch(href, { ...init, headers });
    const contentType = res.headers.get("content-type") ?? "";
    // drop potential charset (+ other name/value pairs) as parser needs the mediaType
    const mediaType = contentType.split(";", 1)[0]!.trim();
    // the DOMParser can handle two types of HTML
    if (mediaType !== "text/html" && mediaType !== "application/xhtml+xml") {
      // everything else (e.g. audio/mp3) will be handled by the browser but not by us
      return null;
    }
    const html = await res.text();
    // exactOptionalPropertyTypes: true forbids assigning `undefined` to a `redirected?: string`
    // slot, so omit the property when not redirected instead of setting it to undefined.
    return res.redirected ? { html, redirected: res.url, mediaType } : { html, mediaType };
  } catch {
    // can't fetch, let someone else deal with it.
    return null;
  }
}

export function getFallback(): Fallback {
  const el = document.querySelector('[name="zfb-view-transitions-fallback"]');
  if (el) {
    // Astro casts `content` straight to Fallback; harden against an arbitrary/typo'd
    // attribute value by validating against the known set and defaulting to "animate".
    const content = el.getAttribute("content");
    if (content === "none" || content === "animate" || content === "swap") {
      return content;
    }
  }
  return "animate";
}

// Upper bound (ms) for waiting on a single external <script>'s load/error in
// runScripts(). Not an Astro upstream value — added so a slow/broken external
// script can never hang the transition indefinitely.
const EXTERNAL_SCRIPT_WAIT_TIMEOUT = 5000;

function runScripts() {
  let wait = Promise.resolve();
  let needsWaitForInlineModuleScript = false;
  // The original code made the assumption that all inline scripts are directly executed when inserted into the DOM.
  // This is not true for inline module scripts, which are deferred but still executed in order.
  // inline module scripts cannot be awaited for with onload.
  // Thus to be able to wait for the execution of all scripts, we make sure that the last inline module script
  // is always followed by an external module script
  for (const script of document.getElementsByTagName("script")) {
    script.dataset["zfbExec"] === undefined &&
      script.getAttribute("type") === "module" &&
      (needsWaitForInlineModuleScript = script.getAttribute("src") === null);
  }
  needsWaitForInlineModuleScript &&
    document.body.insertAdjacentHTML(
      "beforeend",
      `<script type="module" src="data:application/javascript,"/>`,
    );

  for (const script of document.getElementsByTagName("script")) {
    if (script.dataset["zfbExec"] === "") continue;
    const type = script.getAttribute("type");
    if (type && type !== "module" && type !== "text/javascript") continue;
    const newScript = document.createElement("script");
    newScript.innerHTML = script.innerHTML;
    for (const attr of script.attributes) {
      if (attr.name === "src") {
        // Astro awaits onload/onerror with no upper bound; a slow or broken
        // external <script> would then hang the whole transition. Race the
        // load against a timeout so the transition can always proceed.
        const p = new Promise<void>((r) => {
          let timer: ReturnType<typeof setTimeout>;
          const done = () => {
            clearTimeout(timer);
            r();
          };
          newScript.onload = newScript.onerror = done;
          timer = setTimeout(done, EXTERNAL_SCRIPT_WAIT_TIMEOUT);
        });
        wait = wait.then(() => p as any);
      }
      newScript.setAttribute(attr.name, attr.value);
    }
    newScript.dataset["zfbExec"] = "";
    script.replaceWith(newScript);
  }
  return wait;
}

// Add a new entry to the browser history. This also sets the new page in the browser address bar.
// Sets the scroll position according to the hash fragment of the new location.
const moveToLocation = (
  to: URL,
  from: URL,
  options: Options,
  pageTitleForBrowserHistory: string,
  historyState?: State,
  // True when transition() already committed a history entry for THIS
  // navigation before the swap ran (the WebKit-workaround early commit
  // below). `to` here may differ from that committed entry's URL if a
  // `zfb:before-swap` listener mutated `event.to` (writable per Astro
  // parity) after the early commit already ran — in that case we must
  // REPLACE the already-committed entry instead of pushing a second one, or
  // a single navigation would leave two history entries (a phantom Back
  // stop). #1398.
  historyCommittedEarly = false,
) => {
  const intraPage = samePage(from, to);

  const targetPageTitle = document.title;
  document.title = pageTitleForBrowserHistory;

  let scrolledToTop = false;
  if (to.href !== location.href && !historyState) {
    if (options.history === "replace" || historyCommittedEarly) {
      // Astro reads current.index/scrollX/scrollY directly; `history.state` can be
      // null (page entered without a transition state), which would throw a
      // TypeError. Fall back to a synthesized state from the tracked index/scroll.
      const current = history.state ?? {
        index: currentHistoryIndex,
        scrollX,
        scrollY,
      };
      safeReplaceState(
        {
          ...options.state,
          index: current.index,
          scrollX: current.scrollX,
          scrollY: current.scrollY,
        },
        "",
        to.href,
      );
    } else {
      safePushState(
        { ...options.state, index: ++currentHistoryIndex, scrollX: 0, scrollY: 0 },
        "",
        to.href,
      );
    }
  }
  document.title = targetPageTitle;
  // now we are on the new page for non-history navigation!
  // (with history navigation page change happens before popstate is fired)
  originalLocation = to;

  // freshly loaded pages start from the top
  if (!intraPage) {
    scrollTo({ left: 0, top: 0, behavior: "instant" });
    scrolledToTop = true;
  }

  if (historyState) {
    // Raw-History consumers may push partial/empty state (`{}`), so the tracked
    // scroll fields can be absent or non-finite. Never scrollTo(undefined, …) —
    // leave scroll where it is rather than jumping to (0,0). #1376.
    if (Number.isFinite(historyState.scrollX) && Number.isFinite(historyState.scrollY)) {
      scrollTo(historyState.scrollX, historyState.scrollY);
    }
  } else {
    if (to.hash) {
      // because we are already on the target page ...
      // ... what comes next is an intra-page navigation
      // that won't reload the page but instead scroll to the fragment
      history.scrollRestoration = "auto";
      const savedState = history.state;
      location.href = to.href; // this kills the history state on Firefox
      if (!history.state) {
        safeReplaceState(savedState, ""); // this restores the history state
        if (intraPage) {
          window.dispatchEvent(new PopStateEvent("popstate"));
        }
      }
    } else {
      if (!scrolledToTop) {
        scrollTo({ left: 0, top: 0, behavior: "instant" });
      }
    }
    history.scrollRestoration = "manual";
  }
};

let syncHistoryEntryOnServerWarned = false;

/**
 * Write a router-managed history entry (push or replace) WITHOUT any
 * navigation, DOM, or lifecycle side effect. This is the supported path for
 * consumers deep-linking transient UI state (dialogs/modals, a photo
 * viewer's `/photos/<slug>/` URL). Hand-rolled raw `history.pushState`
 * desyncs `originalLocation` and the index bookkeeping that popstate
 * direction detection ({@link derivePopDirection}) and the same-page
 * traverse fast-path depend on; `navigate()` can't be used instead because
 * it forces a fetch. See #1377 / #1374.
 *
 * Never scrolls the viewport itself — but the entry it writes IS stamped
 * with the CURRENT scroll position (`scrollX`/`scrollY` at call time), not
 * `(0, 0)` (issue #1398). This matters only for a later Forward-traversal
 * back to this entry: the same-page traverse fast-path restores whatever
 * scroll position was stamped on the entry, so a same-page push (e.g.
 * opening a dialog) does not snap the underlying page to the top when the
 * dialog is later reopened via Forward.
 *
 * @param url - The URL to write. Cross-origin URLs throw rather than
 *   silently falling back to a full-page load.
 * @param options.replace - Use `history.replaceState` instead of
 *   `history.pushState` (no new Back-button entry). Default: push.
 * @param options.state - Merged into the entry's `history.state`; the
 *   router's own bookkeeping keys (`index`, `scrollX`, `scrollY`) always win
 *   on a colliding key.
 */
export function syncHistoryEntry(url: string | URL, options: SyncHistoryEntryOptions = {}): void {
  // SSR guard: no window/history to touch. Mirror navigate()'s server no-op +
  // one-time console warning. Checked at call time via `typeof document` (not the
  // module-eval `inBrowser` const) so the branch is reachable and behavior is
  // identical in a real environment (document is always present in a browser).
  if (typeof document === "undefined") {
    if (!syncHistoryEntryOnServerWarned) {
      // instantiate an error for the stacktrace to show to user.
      const warning = new Error(
        "syncHistoryEntry() was called during a server side render. This may be unintentional as it is expected to be called in response to a client-side user interaction. Please make sure that your usage is correct.",
      );
      warning.name = "Warning";
      // biome-ignore lint/suspicious/noConsole: allowed
      console.warn(warning);
      syncHistoryEntryOnServerWarned = true;
    }
    return;
  }

  // Adopt this page's existing history index before stamping an entry — an
  // init-less caller must not reset a live entry's index to the module default.
  ensureNavigationState();

  const to = new URL(url, location.href);

  // Cross-origin is not ours to manage. The History API would throw a native
  // SecurityError for a cross-origin URL, but we guard explicitly so the failure
  // is a clear, documented contract violation rather than an opaque platform
  // error — and never a silent full-page load. #1377.
  if (to.origin !== location.origin) {
    throw new Error(
      `syncHistoryEntry(): refusing to write a cross-origin history entry for "${to.href}" (current origin: ${location.origin}).`,
    );
  }

  if (options.replace) {
    // Replace keeps the current entry's index. A consumer migrating from raw
    // history.pushState may have left a missing/invalid index, so fall back to
    // the tracked index rather than stamping NaN/undefined.
    const current = history.state;
    const index =
      current != null && Number.isFinite(current.index) ? current.index : currentHistoryIndex;
    safeReplaceState(
      {
        ...options.state,
        index,
        scrollX: Number.isFinite(current?.scrollX) ? current.scrollX : scrollX,
        scrollY: Number.isFinite(current?.scrollY) ? current.scrollY : scrollY,
      },
      "",
      to.href,
    );
  } else {
    // Persist the CURRENT (outgoing) entry's latest scroll before pushing, so a
    // later Back restoration can't lose scroll that `scrollend` hasn't flushed
    // yet — the same guard transition() applies before a non-traverse push.
    updateScrollPosition({ scrollX, scrollY });
    // Stamp the CURRENT scroll on the freshly-pushed entry too — NOT (0,0).
    // This is a same-page push (dialog/photo-viewer pattern): the underlying
    // page never actually scrolled anywhere, it just gained an overlay. A
    // (0,0)-stamped entry would make the traverse fast-path (moveToLocation's
    // historyState branch below) scrollTo(0,0) when this entry is later
    // Forward-reopened, snapping the page to the top under the reopened
    // dialog. #1398.
    safePushState(
      { ...options.state, index: ++currentHistoryIndex, scrollX, scrollY },
      "",
      to.href,
    );
  }

  // Always re-point originalLocation so the next transition()/onPopState uses the
  // correct "from" URL. Skipping this is exactly what makes a raw pushState
  // desync the router (a same-page Back would miss the traverse fast path).
  originalLocation = to;
}

function preloadStyleLinks(newDocument: Document) {
  const links: Promise<any>[] = [];
  for (const el of newDocument.querySelectorAll("head link[rel=stylesheet]")) {
    const persistId = el.getAttribute(PERSIST_ATTR);
    const href = el.getAttribute("href");
    const existingSelectors = [
      persistId !== null ? attrValueSelector(PERSIST_ATTR, persistId) : null,
      href !== null ? `link[rel=stylesheet]${attrValueSelector("href", href)}` : null,
    ].filter((selector): selector is string => selector !== null);
    const existingLink =
      (existingSelectors.length ? document.querySelector(existingSelectors.join(", ")) : null) ??
      (persistId !== null
        ? querySelectorWithAttrValue(document, "", PERSIST_ATTR, persistId)
        : null) ??
      (href !== null
        ? querySelectorWithAttrValue(document, "link[rel=stylesheet]", "href", href)
        : null);

    // Do not preload links that are already on the page.
    if (href !== null && !existingLink) {
      const c = document.createElement("link");
      c.setAttribute("rel", "preload");
      c.setAttribute("as", "style");
      c.setAttribute("href", href);
      links.push(
        new Promise<any>((resolve) => {
          ["load", "error"].forEach((evName) => c.addEventListener(evName, resolve));
          document.head.append(c);
        }),
      );
    }
  }
  return links;
}

// replace head and body of the windows document with contents from newDocument
// if !popstate, update the history entry and scroll position according to toLocation
// if popState is given, this holds the scroll position for history navigation
// if fallback === "animate" then simulate view transitions
async function updateDOM(
  preparationEvent: TransitionBeforePreparationEvent,
  options: Options,
  currentTransition: Transition,
  historyState?: State,
  fallback?: Fallback,
  // Forwarded to moveToLocation() — see its parameter doc. #1398.
  historyCommittedEarly = false,
) {
  async function animate(phase: string) {
    function isInfinite(animation: Animation) {
      const effect = animation.effect;
      if (!effect || !(effect instanceof KeyframeEffect) || !effect.target) return false;
      const style = window.getComputedStyle(effect.target, effect.pseudoElement);
      return style.animationIterationCount === "infinite";
    }
    const currentAnimations = document.getAnimations();
    // Trigger view transition animations waiting for data-zfb-transition-fallback
    document.documentElement.setAttribute(OLD_NEW_ATTR, phase);
    const nextAnimations = document.getAnimations();
    const newAnimations = nextAnimations.filter(
      (a) => !currentAnimations.includes(a) && !isInfinite(a),
    );
    // Wait for all new animations to finish (resolved or rejected).
    // Do not reject on canceled ones.
    return Promise.allSettled(newAnimations.map((a) => a.finished));
  }

  const animateFallbackOld = async () => {
    if (
      fallback === "animate" &&
      !currentTransition.transitionSkipped &&
      !preparationEvent.signal.aborted
    ) {
      try {
        await animate("old");
      } catch {
        // animate might reject as a consequence of a call to skipTransition()
        // ignored on purpose
      }
    }
  };

  const pageTitleForBrowserHistory = document.title; // document.title will be overridden by swap()
  // Cancel deferred-hydration callbacks for old-body islands before the swap
  // so rIC / IntersectionObserver fires do not run against orphan elements.
  // Called before doSwap() which dispatches `zfb:before-swap` then mutates the DOM.
  cancelPendingIslands();
  // Unmount mounted islands on the OLD body before the swap so Preact/React
  // trees receive render(null, element) / root.unmount() and their useEffect
  // cleanups fire. Must happen after cancelPendingIslands() and before doSwap()
  // so document.body still points to the old body.
  //
  // Pass the incoming body so unmountIslands can SKIP islands that swapBodyElement
  // will lift into the new body (a persisted marker matched on both sides). Those
  // nodes are physically moved, not discarded, so their component instance and
  // state must survive the swap — unmounting them here would empty the container
  // before the lift and defeat data-zfb-transition-persist entirely (issue #1389).
  unmountIslands(document.body, preparationEvent.newDocument.body);
  const swapEvent = await doSwap(
    preparationEvent,
    currentTransition.viewTransition!,
    animateFallbackOld,
  );
  moveToLocation(
    swapEvent.to,
    swapEvent.from,
    options,
    pageTitleForBrowserHistory,
    historyState,
    historyCommittedEarly,
  );
  triggerEvent("zfb:after-swap");

  // Resolve the finished promise of the simulation's ViewTransition.
  // For 'animate', wait for the new-page animation to complete first.
  // For other fallback modes (e.g. 'swap'), resolve immediately — no animation needed.
  if (fallback === "animate" && !currentTransition.transitionSkipped && !swapEvent.signal.aborted) {
    animate("new").finally(() => currentTransition.viewTransitionFinished!());
  } else {
    currentTransition.viewTransitionFinished?.();
  }
}

function abortAndRecreateMostRecentNavigation(): Navigation {
  mostRecentNavigation?.controller.abort();
  return (mostRecentNavigation = {
    controller: new AbortController(),
  });
}

async function transition(
  direction: Direction,
  from: URL,
  to: URL,
  options: Options,
  historyState?: State,
  hasUAVisualTransition = false,
) {
  // The most recent navigation always has precedence
  // Yes, there can be several navigation instances as the user can click links
  // while we fetch content or simulate view transitions. Even synchronous creations are possible
  // e.g. by calling navigate() from a transition event.
  // Invariant: all but the most recent navigation are already aborted.

  const currentNavigation = abortAndRecreateMostRecentNavigation();

  // not ours
  if (!transitionEnabledOnThisPage() || location.origin !== to.origin) {
    if (currentNavigation === mostRecentNavigation) mostRecentNavigation = undefined;
    location.href = to.href;
    return;
  }

  const navigationType = historyState
    ? "traverse"
    : options.history === "replace"
      ? "replace"
      : "push";

  if (navigationType !== "traverse") {
    updateScrollPosition({ scrollX, scrollY });
  }
  if (samePage(from, to) && !options.formData) {
    if (
      // Same-page Back/Forward: serve from the live DOM (no re-fetch/re-swap)
      // unless this page opted back into the fetch (per-request SSR). #1374/#1376.
      (navigationType === "traverse" && !traverseRefetchOptedOut()) ||
      (direction !== "back" && to.hash) ||
      (direction === "back" && from.hash)
    ) {
      moveToLocation(to, from, options, document.title, historyState);
      if (currentNavigation === mostRecentNavigation) mostRecentNavigation = undefined;
      return;
    }
  }

  const prepEvent = await doPreparation(
    from,
    to,
    direction,
    navigationType,
    options.sourceElement,
    options.info,
    currentNavigation!.controller.signal,
    options.formData,
    defaultLoader,
  );
  if (prepEvent.defaultPrevented || prepEvent.signal.aborted) {
    if (currentNavigation === mostRecentNavigation) mostRecentNavigation = undefined;
    triggerEvent("zfb:navigation-aborted");
    if (!prepEvent.signal.aborted) {
      // not aborted -> delegate to browser
      location.href = to.href;
    }
    // and / or exit
    return;
  }

  async function defaultLoader(preparationEvent: TransitionBeforePreparationEvent) {
    const href = preparationEvent.to.href;
    const init: RequestInit = { signal: preparationEvent.signal };
    if (preparationEvent.formData) {
      init.method = "POST";
      const form =
        preparationEvent.sourceElement instanceof HTMLFormElement
          ? preparationEvent.sourceElement
          : preparationEvent.sourceElement instanceof HTMLElement &&
              "form" in preparationEvent.sourceElement
            ? (preparationEvent.sourceElement.form as HTMLFormElement)
            : preparationEvent.sourceElement?.closest("form");
      // Form elements without enctype explicitly set default to application/x-www-form-urlencoded.
      // In order to maintain compatibility with Astro 4.x, we need to check the value of enctype
      // on the attributes property rather than accessing .enctype directly. Astro 5.x may
      // introduce defaulting to application/x-www-form-urlencoded as a breaking change, and then
      // we can access .enctype directly.
      //
      // Note: getNamedItem can return null in real life, even if TypeScript doesn't think so, hence
      // the ?.
      // `form` can be null (closest() miss, or a form-less form-associated
      // element) — `!= null` covers both null and undefined so Reflect.get
      // is never invoked on a non-element receiver ("Illegal invocation").
      init.body =
        form != null &&
        Reflect.get(HTMLFormElement.prototype, "attributes", form).getNamedItem("enctype")
          ?.value === "application/x-www-form-urlencoded"
          ? new URLSearchParams(preparationEvent.formData as any)
          : preparationEvent.formData;
    }
    const response = await fetchHTML(href, init);
    // If there is a problem fetching the new page, just do an MPA navigation to it.
    if (response === null) {
      preparationEvent.preventDefault();
      return;
    }
    // if there was a redirection, show the final URL in the browser's address bar
    if (response.redirected) {
      const redirectedTo = new URL(response.redirected);
      // but do not redirect cross origin
      if (redirectedTo.origin !== preparationEvent.to.origin) {
        preparationEvent.preventDefault();
        return;
      }
      // preserve fragment
      const fragment = preparationEvent.to.hash;
      preparationEvent.to = redirectedTo;
      preparationEvent.to.hash = fragment;
    }

    parser ??= new DOMParser();

    preparationEvent.newDocument = parser.parseFromString(response.html, response.mediaType);
    // The next line might look like a hack,
    // but it is actually necessary as noscript elements
    // and their contents are returned as markup by the parser,
    // see https://developer.mozilla.org/en-US/docs/Web/API/DOMParser/parseFromString
    preparationEvent.newDocument.querySelectorAll("noscript").forEach((el) => el.remove());

    // If ClientRouter is not enabled on the incoming page, do a full page load to it.
    // Unless this was a form submission, in which case we do not want to trigger another mutation.
    if (
      !preparationEvent.newDocument.querySelector('[name="zfb-view-transitions-enabled"]') &&
      !preparationEvent.formData
    ) {
      preparationEvent.preventDefault();
      return;
    }

    const links = preloadStyleLinks(preparationEvent.newDocument);
    links.length && !preparationEvent.signal.aborted && (await Promise.all(links));

    // W3C2: prepareForClientOnlyComponents() goes here. Astro's DEV-only iframe
    // trick that hoists Vite per-component CSS for client:only islands does not
    // apply to zfb (W1B §13.5 — zfb islands inject CSS via the bundle), so the
    // call is intentionally absent rather than stubbed.
  }
  async function abortAndRecreateMostRecentTransition(): Promise<Transition> {
    if (mostRecentTransition) {
      if (mostRecentTransition.viewTransition) {
        try {
          mostRecentTransition.viewTransition.skipTransition();
        } catch {
          // might throw AbortError DOMException. Ignored on purpose.
        }
        try {
          // UpdateCallbackDone might already been settled, i.e. if the previous transition finished updating the DOM.
          // Could not take long, we wait for it to avoid parallel updates
          // (which are very unlikely as long as swap() is not async).
          await mostRecentTransition.viewTransition.updateCallbackDone;
        } catch {
          // There was an error in the update callback of the transition which we cancel.
          // Ignored on purpose
        }
      }
    }
    return (mostRecentTransition = { transitionSkipped: false });
  }

  const currentTransition = await abortAndRecreateMostRecentTransition();

  if (prepEvent.signal.aborted) {
    if (currentNavigation === mostRecentNavigation) mostRecentNavigation = undefined;
    return;
  }

  document.documentElement.setAttribute(DIRECTION_ATTR, prepEvent.direction);

  // Commit the SPA history entry BEFORE the transition's update callback runs.
  //
  // WebKit (all iOS browsers, incl. iOS Chrome) does not reliably commit a
  // distinct back/forward history entry when history.pushState() is issued
  // *inside* the document.startViewTransition() update callback: the URL bar
  // updates but no new entry is created, so a single Back press falls off the
  // site to the browser home (zudolab/zzmod#662). Committing the entry here —
  // outside the transition's update callback — makes WebKit create it; Chromium
  // is unaffected either way. moveToLocation() (called later from updateDOM,
  // inside the callback) then no-ops its own push/replace via the
  // `to.href !== location.href` guard, since location is already at the
  // committed URL, so currentHistoryIndex is not double-advanced.
  //
  // We use prepEvent.to (redirects are already resolved into it during
  // preparation) and write before the swap, while document.title is still the
  // old page's title — so the back entry keeps the correct (old-page) title
  // without needing moveToLocation's title juggle. Writing here also covers the
  // non-VT fallback path below in one place.
  //
  // Traverse (popstate) navigations carry historyState: the browser has already
  // moved, so they must NOT create a new entry here.
  //
  // Tracks whether this block ran (push OR replace) so moveToLocation (called
  // later, from updateDOM) knows a commit already happened for THIS navigation
  // — see moveToLocation's `historyCommittedEarly` parameter doc. #1398.
  let historyCommittedEarly = false;
  if (!historyState && prepEvent.to.href !== location.href) {
    historyCommittedEarly = true;
    if (options.history === "replace") {
      // Mirror of the moveToLocation replace-path guard: `history.state` can be
      // null here too (page entered without a transition state), so synthesize a
      // state from the tracked index/scroll instead of dereferencing null.
      const current = history.state ?? {
        index: currentHistoryIndex,
        scrollX,
        scrollY,
      };
      safeReplaceState(
        {
          ...options.state,
          index: current.index,
          scrollX: current.scrollX,
          scrollY: current.scrollY,
        },
        "",
        prepEvent.to.href,
      );
    } else {
      safePushState(
        { ...options.state, index: ++currentHistoryIndex, scrollX: 0, scrollY: 0 },
        "",
        prepEvent.to.href,
      );
    }
  }

  if (supportsViewTransitions && !hasUAVisualTransition) {
    // This automatically cancels any previous transition
    // We also already took care that the earlier update callback got through
    currentTransition.viewTransition = document.startViewTransition(
      async () =>
        await updateDOM(
          prepEvent,
          options,
          currentTransition,
          historyState,
          undefined,
          historyCommittedEarly,
        ),
    );
  } else {
    // Simulation mode requires a bit more manual work.
    // Also used when PopStateEvent.hasUAVisualTransition indicates the browser already
    // provided a visual transition (e.g. Safari swipe gesture) — in that case, fallback
    // is "swap" to skip animations.
    const updateDone = (async () => {
      // Immediately paused to set up the ViewTransition object for Fallback mode
      await Promise.resolve(); // hop through the micro task queue
      await updateDOM(
        prepEvent,
        options,
        currentTransition,
        historyState,
        hasUAVisualTransition ? "swap" : getFallback(),
        historyCommittedEarly,
      );
      return undefined;
    })();

    // When the updateDone promise is settled,
    // we have run and awaited all swap functions and the after-swap event
    // This qualifies for "updateCallbackDone".
    //
    // For the build in ViewTransition, "ready" settles shortly after "updateCallbackDone",
    // i.e. after all pseudo elements are created and the animation is about to start.
    // In simulation mode the "old" animation starts before swap,
    // the "new" animation starts after swap. That is not really comparable.
    // Thus we go with "very, very shortly after updateCallbackDone" and make both equal.
    //
    // "finished" resolves after all animations are done.

    currentTransition.viewTransition = {
      updateCallbackDone: updateDone, // this is about correct
      ready: updateDone, // good enough
      // Finished promise could have been done better: finished rejects iff updateDone does.
      // Our simulation always resolves, never rejects.
      finished: new Promise((r) => (currentTransition.viewTransitionFinished = r as () => void)), // see end of updateDOM
      skipTransition: () => {
        currentTransition.transitionSkipped = true;
        // This cancels all animations of the simulation
        document.documentElement.removeAttribute(OLD_NEW_ATTR);
      },
      types: new Set<string>(), // empty by default
    };
  }
  // In earlier versions was then'ed on viewTransition.ready which would not execute
  // if the visual part of the transition has errors or was skipped
  currentTransition.viewTransition?.updateCallbackDone.finally(async () => {
    await runScripts();
    // Mount new island markers introduced by the body swap. Fire-and-forget;
    // each island's scheduleHydrate call is async (idle / visible). Called after
    // runScripts() so any new mountIslands() registration from inline scripts in
    // the new page has already run. Called before onPageLoad() per W1B §12.2.
    mountNewIslands();
    onPageLoad();
    announce();
  });
  // finished.ready and finished.finally are the same for the simulation but not
  // necessarily for native view transition, where finished rejects when updateCallbackDone does.
  currentTransition.viewTransition?.finished.finally(() => {
    // exactOptionalPropertyTypes: true forbids assigning `undefined` to an optional
    // `viewTransition?: ViewTransition` slot — `delete` is the equivalent reset.
    delete currentTransition.viewTransition;
    if (currentTransition === mostRecentTransition) mostRecentTransition = undefined;
    if (currentNavigation === mostRecentNavigation) mostRecentNavigation = undefined;
    document.documentElement.removeAttribute(DIRECTION_ATTR);
    document.documentElement.removeAttribute(OLD_NEW_ATTR);
  });
  try {
    // Compatibility:
    // In an earlier version we awaited viewTransition.ready, which includes animation setup.
    // Scripts that depend on the view transition pseudo elements should hook on viewTransition.ready.
    await currentTransition.viewTransition?.updateCallbackDone;
  } catch (e) {
    // This log doesn't make it worse than before, where we got error messages about uncaught exceptions, which can't be caught when the trigger was a click or history traversal.
    // Needs more investigation on root causes if errors still occur sporadically
    const err = e as Error;
    // biome-ignore lint/suspicious/noConsole: allowed
    console.log("[zfb]", err.name, err.message, err.stack);
  }
}

let navigateOnServerWarned = false;

export async function navigate(href: string, options?: Options) {
  if (inBrowser === false) {
    if (!navigateOnServerWarned) {
      // instantiate an error for the stacktrace to show to user.
      const warning = new Error(
        "The view transitions client API was called during a server side render. This may be unintentional as the navigate() function is expected to be called in response to user interactions. Please make sure that your usage is correct.",
      );
      warning.name = "Warning";
      // biome-ignore lint/suspicious/noConsole: allowed
      console.warn(warning);
      navigateOnServerWarned = true;
    }
    return;
  }
  if (!supportsViewTransitions && getFallback() === "none") {
    const url = new URL(href, location.href);
    // Honor the history option on the full-load fallback path: "replace"
    // must not leave a back-button entry, so use location.replace().
    if (options?.history === "replace") {
      location.replace(url.href);
    } else {
      location.href = url.href;
    }
    return;
  }
  // `originalLocation` is seeded by init(); seed it here too so an init-less
  // caller never hands transition() an undefined "from" URL.
  ensureNavigationState();
  await transition("forward", originalLocation, new URL(href, location.href), options ?? {});
}

function onPopState(ev: PopStateEvent) {
  if (!transitionEnabledOnThisPage() && ev.state) {
    // The current page doesn't have View Transitions enabled
    // but the page we navigate to does (because it set the state).
    // Do a full page refresh to reload the client-side router from the new page.
    location.reload();
    return;
  }

  // History entries without state are created by the browser (e.g. for hash links)
  // Our view transition entries always have state.
  // Just ignore stateless entries.
  // The browser will handle navigation fine without our help
  if (ev.state === null) {
    return;
  }
  const state: State = history.state;
  const nextIndex = state.index;
  const direction: Direction = derivePopDirection(nextIndex, currentHistoryIndex);
  // Only advance the tracked index when the entry carries a usable index.
  // After a bfcache restore the index can be missing/NaN; clobbering the
  // tracked value with NaN would poison every subsequent direction calc, so
  // keep the last known-good index in that case (see derivePopDirection).
  if (Number.isFinite(nextIndex)) {
    currentHistoryIndex = nextIndex;
  }
  transition(
    direction,
    originalLocation,
    new URL(location.href),
    {},
    state,
    ev.hasUAVisualTransition,
  );
}

// Decide forward vs back from the popped entry's index against the last
// tracked index. Two desync cases a WebKit bfcache restore can produce:
//   - missing / NaN `next` (restored entry lost its index): handled explicitly
//     below — we cannot prove a forward move, so treat it as "back" (the safe
//     default; a forward misclassification is what makes Back skip to the wrong
//     page).
//   - `next === current` (index unchanged after a desync/replace): falls out of
//     the `>` comparison as "back" — not a forward navigation.
function derivePopDirection(nextIndex: number, trackedIndex: number): Direction {
  if (!Number.isFinite(nextIndex)) return "back";
  return nextIndex > trackedIndex ? "forward" : "back";
}

const onScrollEnd = () => {
  // NOTE: our "popstate" event handler may call `pushState()` or
  // `replaceState()` and then `scrollTo()`, which will fire "scroll" and
  // "scrollend" events. To avoid redundant work and expensive calls to
  // `replaceState()`, we simply check that the values are different before
  // updating.
  if (history.state && (scrollX !== history.state.scrollX || scrollY !== history.state.scrollY)) {
    updateScrollPosition({ scrollX, scrollY });
  }
};

// zfb-only addition (no Astro upstream — see file header "zfb-only additions").
// WebKit serves Back navigations after an SPA route change from the bfcache:
// the page is restored without re-evaluating this module or re-running init(),
// so `seedPageState()` below (which sets `currentHistoryIndex` from
// `history.state.index`) never re-runs and the tracked index can desync from
// the live history stack. A desynced index makes onPopState's direction calc
// misfire, so Back skips an entry. On a persisted (bfcache) restore we re-seed
// the tracked index and the `originalLocation` "from" URL from the live state,
// and restore scroll — mirroring that seed. This is a no-op on a normal load
// (`persisted` falsy/absent) and idempotent (re-seeding from the same state
// twice changes nothing and fires no transition).
const onPageShow = (ev: PageTransitionEvent) => {
  // Normal (non-bfcache) loads already ran init()'s seed; leave them be.
  if (!ev.persisted) return;
  // init() seeds `originalLocation` and `currentHistoryIndex` together; re-sync
  // both here so the next transition() gets the correct `from` URL (a stale
  // `originalLocation` would feed onPopState the wrong origin).
  originalLocation = new URL(location.href);
  const index = history.state?.index;
  if (Number.isFinite(index)) {
    currentHistoryIndex = index;
  }
  if (history.state) {
    scrollTo({ left: history.state.scrollX, top: history.state.scrollY });
  }
};

// ---- initialization (folded out of module scope by #2436) ----

// Once-guard for the page-state phase of init(). Deliberately SEPARATE from the
// `initialized` latch below, because the two phases have different eligibility:
// this one runs on EVERY browser page — including one where view transitions
// are ineligible (no native support and fallback "none") — while the activation
// listeners are VT-gated. A single shared latch would either skip this phase
// forever after an ineligible early call, or latch activation on a page that
// never qualified for it. (#2436)
let pageStateSeeded = false;

/**
 * Everything module evaluation used to do for EVERY browser page, regardless
 * of view-transition eligibility: restore (or seed) this page's history entry
 * and its scroll position, and mark the scripts the initial page load already
 * executed so runScripts() does not re-run them after a swap.
 *
 * Called from init() BEFORE its eligibility early-return — that gating
 * asymmetry is load-bearing and predates the fold: a page with fallback "none"
 * still needs its history entry restored and its scripts marked. (#2436)
 */
function seedPageState(): void {
  if (pageStateSeeded) return;
  pageStateSeeded = true;

  if (history.state) {
    // Here we reloaded a page with history state
    // (e.g. history navigation from non-transition page or browser reload)
    // Adopt only a finite index — a foreign pre-init history.state (e.g. a
    // consumer's own replaceState({modal: true})) has no router index, and
    // adopting undefined would stamp the next push with NaN. Same guard as
    // ensureNavigationState() and onPageShow(). (#2436)
    const index = history.state.index;
    if (Number.isFinite(index)) currentHistoryIndex = index;
    // Skip the scroll restore when an init-less navigate()/syncHistoryEntry()
    // already ran on this page (navigationStateSeeded latched before init):
    // the entry it pushed is stamped scrollX/scrollY 0, and a late-activating
    // init() must not yank the already-scrolled viewport to the top. (#2436)
    if (!navigationStateSeeded) {
      scrollTo({ left: history.state.scrollX, top: history.state.scrollY });
    }
  } else if (transitionEnabledOnThisPage()) {
    // This page is loaded from the browser address bar or via a link from extern,
    // it needs a state in the history
    safeReplaceState({ index: currentHistoryIndex, scrollX, scrollY }, "");
    history.scrollRestoration = "manual";
  }

  for (const script of document.getElementsByTagName("script")) {
    detectScriptExecuted(script);
    script.dataset["zfbExec"] = "";
  }
}

/**
 * Register the window-level navigation listeners. Called from init() only
 * after the view-transition eligibility check, and only once — the
 * `initialized` latch owns this phase.
 */
function registerNavigationListeners(): void {
  addEventListener("popstate", onPopState);
  addEventListener("load", onPageLoad);
  // Re-sync the tracked history index + scroll on a WebKit bfcache restore;
  // a no-op on normal loads. See onPageShow above.
  addEventListener("pageshow", onPageShow);
  // There's not a good way to record scroll position before a history back
  // navigation, so we will record it when the user has stopped scrolling.
  if ("onscrollend" in window) addEventListener("scrollend", onScrollEnd);
  else {
    // Keep track of state between intervals
    let intervalId: number | undefined, lastY: number, lastX: number, lastIndex: State["index"];
    const scrollInterval = () => {
      // The interval can outlive its document context — a test-env teardown or a
      // real page unload removes window/history before the 50ms tick. `typeof`
      // never throws even when the global is gone: stop the interval and bail
      // rather than dereferencing a torn-down global (the #1061 bug class; #1063).
      // In a real browser these globals always exist, so this path is test-env
      // only. clearInterval may itself be mid-teardown, so guard the call too.
      if (typeof window === "undefined" || typeof history === "undefined") {
        if (typeof clearInterval === "function") clearInterval(intervalId);
        intervalId = undefined;
        return;
      }
      // Check the index to see if a popstate event was fired
      if (lastIndex !== history.state?.index) {
        clearInterval(intervalId);
        intervalId = undefined;
        return;
      }
      // Check if the user stopped scrolling
      if (lastY === scrollY && lastX === scrollX) {
        // Cancel the interval and update scroll positions
        clearInterval(intervalId);
        intervalId = undefined;
        onScrollEnd();
        return;
      } else {
        ((lastY = scrollY), (lastX = scrollX));
      }
    };
    // We can't know when or how often scroll events fire, so we'll just use them to start intervals
    addEventListener(
      "scroll",
      () => {
        if (intervalId !== undefined) return;
        ((lastIndex = history.state?.index), (lastY = scrollY), (lastX = scrollX));
        intervalId = window.setInterval(scrollInterval, 50);
      },
      { passive: true },
    );
  }
}

// ---- W3C3: click + form intercept, public idempotent init() ----

// Returns true when the modifier-key combo or mouse button means "open in new tab / download".
// Matches Astro's `leavesWindow` helper in ClientRouter.astro.
const leavesWindow = (ev: MouseEvent): boolean =>
  (ev.button !== undefined && ev.button !== 0) || // non-left-click
  ev.metaKey || // new tab (Mac)
  ev.ctrlKey || // new tab (Windows/Linux)
  ev.altKey || // download
  ev.shiftKey; // new window

// Track the last clicked element that will leave the window so form submit can check it.
let lastClickedElementLeavingWindow: EventTarget | null = null;

function handleClick(ev: MouseEvent): void {
  let link: EventTarget | null = ev.target;

  // Record whether this click will leave the window (used by form submit handler).
  lastClickedElementLeavingWindow = leavesWindow(ev) ? link : null;

  // Shadow DOM: prefer composedPath target over ev.target.
  if (ev.composed) {
    link = ev.composedPath()[0] ?? link;
  }

  // Walk up to the nearest <a>, <area>, or <svg:a>.
  if (link instanceof Element) {
    link = link.closest("a, area");
  }

  if (
    !(link instanceof HTMLAnchorElement) &&
    !(link instanceof SVGAElement) &&
    !(link instanceof HTMLAreaElement)
  ) {
    return;
  }

  const linkEl = link as HTMLAnchorElement | SVGAElement | HTMLAreaElement;
  const linkTarget =
    linkEl instanceof HTMLElement ? linkEl.target : (linkEl as SVGAElement).target.baseVal;
  const href = linkEl instanceof HTMLElement ? linkEl.href : (linkEl as SVGAElement).href.baseVal;

  if (!href) return;

  const origin = new URL(href, location.href).origin;

  if (
    // data-zfb-reload: caller wants a full browser reload, not a SPA transition.
    (linkEl as HTMLElement).dataset["zfbReload"] !== undefined ||
    // download attribute: let browser handle download.
    linkEl.hasAttribute("download") ||
    // Non-self target opens in a new context — skip.
    (linkTarget && linkTarget !== "_self") ||
    // Cross-origin: not ours to handle.
    origin !== location.origin ||
    // Modifier key / non-left-click combo: user wants new tab / window / download.
    lastClickedElementLeavingWindow !== null ||
    // Another handler already handled this event.
    ev.defaultPrevented
  ) {
    return;
  }

  ev.preventDefault();
  navigate(href, {
    // data-zfb-history="replace" opts a link into replaceState instead of pushState.
    history: (linkEl as HTMLElement).dataset["zfbHistory"] === "replace" ? "replace" : "auto",
    sourceElement: linkEl,
  });
}

function handleSubmit(ev: SubmitEvent): void {
  const el = ev.target as HTMLElement;
  const submitter = ev.submitter as HTMLElement | null;

  // If the submit was triggered by a modifier-key click, treat as normal browser submit.
  const clickedWithKeys = submitter !== null && submitter === lastClickedElementLeavingWindow;
  lastClickedElementLeavingWindow = null;

  if (
    el.tagName !== "FORM" ||
    ev.defaultPrevented ||
    el.dataset["zfbReload"] !== undefined ||
    clickedWithKeys
  ) {
    return;
  }

  const form = el as HTMLFormElement;
  const formData = new FormData(form, submitter ?? undefined);

  // form.action / form.method can be shadowed by <input name="action"> / <input name="method">,
  // so fall back to getAttribute() when the property is not a string. (Astro's comment.)
  const formAction = typeof form.action === "string" ? form.action : form.getAttribute("action");
  const formMethod = typeof form.method === "string" ? form.method : form.getAttribute("method");

  // Resolve action: submitter formaction attr overrides form action, fallback to current path.
  let action = submitter?.getAttribute("formaction") ?? formAction ?? location.pathname;
  // Resolve method: submitter formmethod attr overrides form method, fallback to "get".
  const method = submitter?.getAttribute("formmethod") ?? formMethod ?? "get";

  // The "dialog" method is a special keyword used within <dialog> elements —
  // https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#attr-fs-method
  if (method === "dialog" || location.origin !== new URL(action, location.href).origin) {
    // No SPA transition in these cases — let browser handle it.
    return;
  }

  const options: import("./types.js").Options = { sourceElement: submitter ?? form };
  if (method === "get") {
    const params = new URLSearchParams(formData as any);
    const url = new URL(action, location.href);
    url.search = params.toString();
    action = url.toString();
  } else {
    options.formData = formData;
  }

  ev.preventDefault();
  navigate(action, options);
}

export interface InitOptions {
  /** Reserved for forward-compat with Astro's prefetch integration. Ignored in v1. */
  prefetchAll?: boolean;
}

// Guard flag — ensures the activation listeners (popstate/load/pageshow/scroll
// plus click + submit) are registered only once even if init() is called
// multiple times (e.g. two <ClientRouter> mounts on the same page).
let initialized = false;

/**
 * Bootstrap the client router on this page: restore its history entry, mark
 * the already-executed scripts, and wire up the navigation, click and
 * form-submit listeners. Safe to call multiple times — each of the two phases
 * runs at most once (idempotent).
 *
 * Evaluating this module does nothing; every side effect the router performs
 * at startup happens here (#2436). Two once-guards rather than one, because
 * the phases have different eligibility — see `pageStateSeeded`.
 *
 * @param _options - Forward-compat hook matching Astro's init() signature. Ignored in v1.
 */
export function init(_options?: InitOptions): void {
  if (!inBrowser) return;

  // Phase 1 — runs on every browser page, even a view-transition-ineligible
  // one, and therefore BEFORE the eligibility early-return below.
  seedPageState();

  if (initialized) return;

  // Phase 2 — activation. Latch the guard only AFTER this eligibility check:
  // an ineligible early call (fallback "none" and no native view transitions)
  // must not permanently latch `initialized`, or a later legitimately-eligible
  // init() would no-op forever.
  if (!supportsViewTransitions && getFallback() === "none") return;

  initialized = true;

  // Where we came from, for the first transition() of this page. Re-seeded
  // unconditionally at activation (as module evaluation used to do), so it
  // reflects the URL at activation time even if an init-less navigate() or
  // syncHistoryEntry() already primed it via ensureNavigationState().
  navigationStateSeeded = true;
  originalLocation = new URL(location.href);

  registerNavigationListeners();

  document.addEventListener("click", handleClick);
  document.addEventListener("submit", handleSubmit as EventListener);

  // Prefetch hook intentionally omitted from v1 — see https://github.com/zudolab/zudo-doc/issues/1527
  // (Followup tracker for porting Astro prefetch module).
}
